import React, { useState, useRef, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Stack,
  Avatar,
  alpha,
  CircularProgress,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Send as SendIcon,
  DeleteOutline as ClearIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import AuthContext from '../../contexts/AuthContext';
import { agentAPI } from '../../services/api';

// ─── Typing indicator ────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 0.5 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: '#00d4ff',
            animation: 'tw-bounce 1.2s infinite',
            animationDelay: `${i * 0.2}s`,
            '@keyframes tw-bounce': {
              '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
              '40%': { transform: 'scale(1)', opacity: 1 },
            },
          }}
        />
      ))}
    </Stack>
  );
}

// ─── Single message bubble ────────────────────────────────────────────────────
function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';

  return (
    <Stack
      direction="row"
      spacing={1.5}
      justifyContent={isUser ? 'flex-end' : 'flex-start'}
      alignItems="flex-end"
      sx={{ mb: 1.5, px: 1 }}
    >
      {!isUser && (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'transparent',
            border: `1px solid ${alpha('#00d4ff', 0.4)}`,
            flexShrink: 0,
          }}
        >
          <BotIcon sx={{ fontSize: 18, color: '#00d4ff' }} />
        </Avatar>
      )}

      <Box
        sx={{
          maxWidth: { xs: '88%', sm: '72%', md: '60%' },
          px: 2,
          py: 1.25,
          borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          bgcolor: isUser
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            : alpha('#1a1f2e', 0.95),
          background: isUser
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            : alpha('#1a1f2e', 0.95),
          border: isUser ? 'none' : `1px solid ${alpha('#00d4ff', 0.15)}`,
          boxShadow: isUser
            ? '0 2px 12px rgba(118,75,162,0.35)'
            : '0 2px 8px rgba(0,0,0,0.3)',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: isUser ? '#fff' : alpha('#fff', 0.88),
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '0.875rem',
          }}
        >
          {msg.text}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.5,
            color: isUser ? alpha('#fff', 0.55) : alpha('#fff', 0.35),
            fontSize: '0.68rem',
            textAlign: isUser ? 'right' : 'left',
          }}
        >
          {msg.time}
        </Typography>
      </Box>

      {isUser && (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: alpha('#667eea', 0.25),
            border: `1px solid ${alpha('#667eea', 0.5)}`,
            flexShrink: 0,
          }}
        >
          <PersonIcon sx={{ fontSize: 18, color: '#667eea' }} />
        </Avatar>
      )}
    </Stack>
  );
}

// ─── Suggestions ─────────────────────────────────────────────────────────────
const SUGGESTIONS = [
  "What were today's trades?",
  "Show me the P&L summary",
  "Which strategies fired today?",
  "Is the system healthy?",
  "Any recent alerts?",
  "What's the win rate this week?",
];

// ─── Main component ───────────────────────────────────────────────────────────
export default function AgentChat() {
  const { user } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to latest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const now = () =>
    new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const send = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;

    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text: trimmed, time: now() }]);
    setLoading(true);

    const result = await agentAPI.chat(trimmed);
    setLoading(false);

    const reply = result.success
      ? result.reply
      : `Sorry, I couldn't reach the agent right now.\n\n${result.error || ''}`;

    setMessages((prev) => [...prev, { role: 'agent', text: reply, time: now() }]);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const clearChat = () => setMessages([]);

  const isEmpty = messages.length === 0;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#0a0e14',
        overflow: 'hidden',
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: `1px solid ${alpha('#00d4ff', 0.1)}`,
          background: 'linear-gradient(90deg, #0a0e14 0%, #0f1624 100%)',
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #00d4ff 0%, #0066ff 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BotIcon sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
                TradeWave Agent
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    bgcolor: '#00ff88',
                    boxShadow: '0 0 6px #00ff88',
                  }}
                />
                <Typography variant="caption" sx={{ color: alpha('#fff', 0.5), fontSize: '0.7rem' }}>
                  Anthropic Managed Agent · always on
                </Typography>
              </Stack>
            </Box>
          </Stack>

          {messages.length > 0 && (
            <Tooltip title="Clear chat">
              <IconButton onClick={clearChat} size="small" sx={{ color: alpha('#fff', 0.4), '&:hover': { color: '#ff4757' } }}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {/* ── Messages ───────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 2, px: 1 }}>
        {isEmpty ? (
          /* Welcome / suggestion screen */
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              px: 3,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #00d4ff22 0%, #0066ff22 100%)',
                border: `1px solid ${alpha('#00d4ff', 0.25)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2.5,
              }}
            >
              <BotIcon sx={{ fontSize: 36, color: '#00d4ff' }} />
            </Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
              Ask me anything
            </Typography>
            <Typography variant="body2" sx={{ color: alpha('#fff', 0.45), mb: 3.5, maxWidth: 380 }}>
              {user?.role === 'admin'
                ? 'Check system health, review logs, query trades, or ask me to run a task.'
                : 'Ask about trade performance, strategy results, alerts, and more.'}
            </Typography>

            {/* Suggestion chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', maxWidth: 520 }}>
              {SUGGESTIONS.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  size="small"
                  onClick={() => send(s)}
                  sx={{
                    bgcolor: alpha('#1a1f2e', 0.9),
                    color: alpha('#fff', 0.7),
                    border: `1px solid ${alpha('#00d4ff', 0.18)}`,
                    fontSize: '0.78rem',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: alpha('#00d4ff', 0.12),
                      color: '#fff',
                      borderColor: alpha('#00d4ff', 0.4),
                    },
                    transition: 'all 0.15s',
                  }}
                />
              ))}
            </Box>
          </Box>
        ) : (
          <>
            {messages.map((msg, i) => (
              <MessageBubble key={i} msg={msg} />
            ))}

            {/* Typing indicator */}
            {loading && (
              <Stack direction="row" spacing={1.5} alignItems="flex-end" sx={{ mb: 1.5, px: 1 }}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: 'transparent',
                    border: `1px solid ${alpha('#00d4ff', 0.4)}`,
                  }}
                >
                  <BotIcon sx={{ fontSize: 18, color: '#00d4ff' }} />
                </Avatar>
                <Paper
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: alpha('#1a1f2e', 0.95),
                    border: `1px solid ${alpha('#00d4ff', 0.15)}`,
                    borderRadius: '16px 16px 16px 4px',
                  }}
                >
                  <TypingDots />
                </Paper>
              </Stack>
            )}
            <div ref={bottomRef} />
          </>
        )}
      </Box>

      {/* ── Input bar ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 2,
          py: 1.75,
          borderTop: `1px solid ${alpha('#00d4ff', 0.1)}`,
          bgcolor: alpha('#0a0e14', 0.98),
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-end">
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={5}
            placeholder="Ask the agent anything…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: alpha('#1a1f2e', 0.8),
                borderRadius: 2.5,
                color: '#fff',
                fontSize: '0.9rem',
                '& fieldset': { borderColor: alpha('#00d4ff', 0.2) },
                '&:hover fieldset': { borderColor: alpha('#00d4ff', 0.4) },
                '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
                '&.Mui-disabled': { opacity: 0.5 },
              },
              '& .MuiInputBase-input::placeholder': { color: alpha('#fff', 0.3) },
            }}
          />
          <IconButton
            onClick={() => send()}
            disabled={!input.trim() || loading}
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              background:
                !input.trim() || loading
                  ? alpha('#1a1f2e', 0.6)
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: !input.trim() || loading ? alpha('#fff', 0.25) : '#fff',
              flexShrink: 0,
              transition: 'all 0.2s',
              '&:hover': {
                background:
                  !input.trim() || loading
                    ? alpha('#1a1f2e', 0.6)
                    : 'linear-gradient(135deg, #7c91f5 0%, #8a5fbf 100%)',
              },
              '&.Mui-disabled': { background: alpha('#1a1f2e', 0.6) },
            }}
          >
            {loading ? (
              <CircularProgress size={18} sx={{ color: alpha('#fff', 0.4) }} />
            ) : (
              <SendIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Stack>
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.75, color: alpha('#fff', 0.2), fontSize: '0.68rem', pl: 0.5 }}
        >
          Shift+Enter for new line · Enter to send
        </Typography>
      </Box>
    </Box>
  );
}
